import { NumericInput } from '@blueprintjs/core'
import { HTMLSelect } from '../ui/html-select'
import classNames from 'classnames'
import { ClassValue } from 'classnames'
import React, { useMemo, useState } from 'react'
import { NumericalChangeActionsE } from '../../constants'
import { NumericalChangeEvent } from '../../types/constants'
import { NumericalChangeActions } from '../../util/contstants'
import { money, percentage } from '../../util/formatters'
import { Button } from '../ui/button'

interface NumericalChangeProps {
    onChange: (e: NumericalChangeEvent) => void
    name?: string
    value?: number | string
    selected?: NumericalChangeActionsE
    disabled?: boolean
    onKeyDown?: (v: any) => void
    inline?: boolean
    fill?: boolean
    buttonPosition?: any
    hiddenOptions?: string[]
    className?: ClassValue
    source?: 'value' | 'variable'
    version?: number
}

// @todo disallow - sign entry
export const NumericalChange = ({
    onChange,
    name = '',
    value = 0,
    selected = NumericalChangeActionsE.DecreasePercent,
    disabled,
    onKeyDown,
    fill = false,
    hiddenOptions = [],
    className,
    source = 'value',
    version = 1,
}: NumericalChangeProps) => {
    const isPercentage =
        selected == NumericalChangeActions.IncreasePercent ||
        selected == NumericalChangeActions.DecreasePercent
    const numericValue = typeof value === 'number' ? value : 0
    const [input, setInput] = useState(numericValue / 100 || '')

    const onValueChange = (_: number, value: string) => {
        const v = parseFloat(value) * 100

        setInput(value)
        onChange({
            action: selected,
            value: Math.round(v),
        })
    }

    const currencySymbol = window.marketplace.currency_symbol

    const options = useMemo(() => {
        const hidden: { [key: string]: boolean } = {}
        hiddenOptions.forEach((o) => (hidden[o] = true))

        return [
            {
                label: `Increase ${name + ' '}(${currencySymbol})`,
                value: NumericalChangeActions.IncreaseDelta,
            },
            { label: `Increase ${name + ' '}(%)`, value: NumericalChangeActions.IncreasePercent },
            { label: `Set ${name + ' '}($)`, value: NumericalChangeActions.Set },
            {
                label: `Decrease ${name + ' '}(${currencySymbol})`,
                value: NumericalChangeActions.DecreaseDelta,
            },
            { label: `Decrease ${name + ' '}(%)`, value: NumericalChangeActions.DecreasePercent },
        ].filter(({ value }) => !hidden[value])
    }, [currencySymbol, hiddenOptions])

    return (
        <div className="flex flex-col">
            <HTMLSelect
                iconProps={{ icon: 'caret-down' }}
                className={classNames(className)}
                fill
                value={selected}
                onChange={(e) => {
                    onChange({
                        action: e.target.value as NumericalChangeActionsE,
                        value: typeof value === 'number' ? value : 0,
                    })
                }}
                options={options}
            />

            <div className="pt-3 pb-0.5">Value Source</div>
            <HTMLSelect
                iconProps={{ icon: 'caret-down' }}
                className={classNames(className)}
                fill
                value={source}
                onChange={(e) => {
                    onChange({
                        action: selected,
                        source: e.target.value as 'value' | 'variable',
                        value: e.target.value === 'value' ? 0 : '',
                    })
                }}
                options={[
                    { label: 'From Value', value: 'value' },
                    { label: 'From Variable', value: 'variable', disabled: version !== 2 },
                ]}
            />
            {/* @todo increase / decrease by flat is setting value*/}
            <div className="pt-3 pb-0.5">Value</div>
            {source === 'value' && isPercentage && (
                <NumericInput
                    disabled={disabled}
                    autoFocus
                    min={0}
                    onKeyDown={(e) => (onKeyDown ? onKeyDown(e) : null)}
                    rightElement={<Button minimal text="%" />}
                    fill
                    placeholder="0.00"
                    value={input}
                    onValueChange={onValueChange}
                />
            )}
            {source === 'value' && !isPercentage && (
                <NumericInput
                    disabled={disabled}
                    autoFocus
                    min={0}
                    onKeyDown={(e) => (onKeyDown ? onKeyDown(e) : null)}
                    leftIcon={<Button minimal text={window.marketplace.currency_symbol} />}
                    fill
                    placeholder="0.00"
                    value={input}
                    onValueChange={onValueChange}
                />
            )}
        </div>
    )
}

export const resolveActionText = (action: any, name: string): string | JSX.Element => {
    let value
    let dir
    let desc

    const isVariable = action?.source === 'variable'

    switch (action.action) {
        case NumericalChangeActions.Set:
            desc = isVariable ? `variable '${action.value}'` : `${money(action.value / 100)}`
            return `Change ${name} to ${desc}`
        case NumericalChangeActions.IncreaseDelta:
        case NumericalChangeActions.DecreaseDelta:
            value = parseFloat(action.value)
            dir =
                action.action == NumericalChangeActions.DecreaseDelta ? (
                    <span className="text-red-500">Decrease</span>
                ) : (
                    <span className="text-green-500">Increase</span>
                )

            desc = isVariable ? `variable '${action.value}'` : `${money(Math.abs(value) / 100)}`
            return (
                <>
                    {dir} {name} by {desc}
                </>
            )
        case NumericalChangeActions.IncreasePercent:
        case NumericalChangeActions.DecreasePercent:
            value = parseFloat(action.value)
            dir =
                action.action == NumericalChangeActions.DecreasePercent ? (
                    <span className="text-red-500">Decrease</span>
                ) : (
                    <span className="text-green-500">Increase</span>
                )

            desc = isVariable
                ? `variable '${action.value}'`
                : `${percentage(Math.abs(value / 100))}`
            return (
                <>
                    {dir} {name} by {desc}
                </>
            )
        default:
            console.warn('Cannot resolve action text for', action)
            return ''
    }
}
